import React from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import { Error, Input, Label, Select, Svg } from 'app/components';
import {
  getPostValidationError,
  getPreValidationError,
  getPreValidationDropdownError,
} from 'app/utils';
import { NavLink, generatePath } from 'react-router-dom';
import routes from 'config/common/routes';

export const FieldRenderProps = {
  input: PropTypes.shape({
    name: PropTypes.string,
    onBlur: PropTypes.func,
    onChange: PropTypes.func,
    onFocus: PropTypes.func,
    value: PropTypes.any
  }),
  meta: PropTypes.shape({
    active: PropTypes.bool,
    dirty: PropTypes.bool,
    dirtySinceLastSubmit: PropTypes.bool,
    error: PropTypes.any,
    initial: PropTypes.bool,
    invalid: PropTypes.bool,
    pristine: PropTypes.bool,
    submitError: PropTypes.any,
    submitFailed: PropTypes.bool,
    submitSucceeded: PropTypes.bool,
    touched: PropTypes.bool,
    valid: PropTypes.bool,
    visited: PropTypes.bool
  })
};

const propTypes = {
  form: PropTypes.objectOf(PropTypes.any).isRequired,
  errors: PropTypes.arrayOf(PropTypes.object),
  dataFields: PropTypes.shape({
    state: PropTypes.shape(FieldRenderProps),
    cinema: PropTypes.shape(FieldRenderProps),
    phoneNumber: PropTypes.shape(FieldRenderProps),
    fax: PropTypes.shape(FieldRenderProps),
    firstName: PropTypes.shape(FieldRenderProps),
    lastName: PropTypes.shape(FieldRenderProps),
    email: PropTypes.shape(FieldRenderProps),
    company: PropTypes.shape(FieldRenderProps),
    department: PropTypes.shape(FieldRenderProps),
    street: PropTypes.shape(FieldRenderProps),
    zipCode: PropTypes.shape(FieldRenderProps),
    city: PropTypes.shape(FieldRenderProps)
  }).isRequired,
  stateOptions: PropTypes.arrayOf(PropTypes.object).isRequired,
  cinemaOptions: PropTypes.arrayOf(PropTypes.object).isRequired,
};

const defaultProps = {
  errors: []
};

const DataInputs = ({ form, errors, dataFields, submitFailed, stateOptions, cinemaOptions }) => {
  const [t] = useTranslation();

  return (
    <div className="cinema-rent__data-inputs-wrapper">
      <div className="cinema-rent__data-header">
        <h2>{t('cinema_rent_inquiry_form.data')}</h2>
        <div className="cinema-rent__data-header-cinema">
          <Svg id="right-arrow-normal" />
          <NavLink
            // to={routes.cinemas.pattern}
            to={{
              pathname: generatePath(routes.cinemas.pattern),
              search: `?date=all`
            }}>
            <h3>{t('cinema_rent_inquiry_form.cinema_overview')}</h3>
          </NavLink>
        </div>
      </div>
      <div className="cinema-rent__inputs-wrapper">
        <div>
          <Label>{t('cinema_rent_inquiry_form.state')} *</Label>
          <Select
            options={stateOptions}
            onChange={value => {
              form.change(dataFields.state.input.name, value);
              form.change(dataFields.cinema.input.name,
                { label: '', value: "" });
            }}
            error={
              getPreValidationDropdownError(dataFields.state, t) ||
              getPostValidationError(errors, dataFields.state.input.name, t)
            }
            onDropdownClose={() => {
              form.getFieldState(dataFields.state.input.name).blur();
            }}
          />
          {submitFailed && (dataFields.state.meta?.error || !dataFields.state.input.value.includes('')) && (
            <Error>
              {(dataFields.state.meta?.error && t(dataFields.state.meta?.error))}
            </Error>
          )}
        </div>
        <div>
          <Label>{t('common.cinema')} *</Label>
          <Select
            multi
            options={cinemaOptions}
            onChange={value => {
              form.change(dataFields.cinema.input.name, value);
            }}
            error={
              getPreValidationDropdownError(dataFields.cinema, t)
              || getPostValidationError(errors, dataFields.cinema.input.name, t)
            }
            onDropdownClose={() => {
              form.getFieldState(dataFields.cinema.input.name).blur();
            }}
            style={{ maxWidth: "300px" }}
          />
          {submitFailed && (dataFields.cinema.meta?.error || !dataFields.cinema.input.value.includes('')) && (
            <Error>
              {/* {(dataFields.cinema.meta?.error && t(dataFields.cinema.meta?.error)) || t('common.select_value_error')} */}
              {(dataFields.cinema.meta?.error && t(dataFields.cinema.meta?.error))}
            </Error>
          )}
        </div>
        <div>
          <Label>{t('cinema_rent_inquiry_form.phone_number')}</Label>
          <Input
            {...dataFields.phoneNumber.input}
            id={dataFields.phoneNumber.input.name}
            type="text"
            error={
              getPreValidationError(dataFields.phoneNumber, t) ||
              getPostValidationError(errors, dataFields.phoneNumber.input.name, t)
            }
          />
        </div>
        <div>
          <Label>{t('cinema_rent_inquiry_form.fax')}</Label>
          <Input
            {...dataFields.fax.input}
            id={dataFields.fax.input.name}
            type="text"
            error={
              getPreValidationError(dataFields.fax, t) ||
              getPostValidationError(errors, dataFields.fax.input.name, t)
            }
          />
        </div>
        <div>
          <Label htmlFor={dataFields.firstName.input.name}>{t('form.firstName')} *</Label>
          <Input
            {...dataFields.firstName.input}
            id={dataFields.firstName.input.name}
            type="text"
            error={
              getPreValidationError(dataFields.firstName, t)
              ||
              getPostValidationError(errors, dataFields.firstName.input.name, t)
            }
          />
          {submitFailed && (dataFields.firstName.meta?.error || !dataFields.firstName.input.value.includes('')) && (
            <Error>
              {(dataFields.firstName.meta?.error && t(dataFields.firstName.meta?.error)) || t('common.select_value_error')}
            </Error>
          )}
        </div>
        <div>
          <Label htmlFor={dataFields.lastName.input.name}>{t('form.lastName')} *</Label>
          <Input
            {...dataFields.lastName.input}
            id={dataFields.lastName.input.name}
            type="text"
            error={
              getPreValidationError(dataFields.lastName, t) ||
              getPostValidationError(errors, dataFields.lastName.input.name, t)
            }
          />
          {submitFailed && (dataFields.lastName.meta?.error || !dataFields.lastName.input.value.includes('')) && (
            <Error>
              {(dataFields.lastName.meta?.error && t(dataFields.lastName.meta?.error)) || t('common.select_value_error')}
            </Error>
          )}
        </div>
        <div>
          <Label>{t('form.email')} *</Label>
          <Input
            {...dataFields.email.input}
            id={dataFields.email.input.name}
            type="text"
            error={
              getPreValidationError(dataFields.email, t) ||
              getPostValidationError(errors, dataFields.email.input.name, t)
            }
          />
          {submitFailed && (dataFields.email.meta?.error || !dataFields.email.input.value.includes('')) && (
            <Error>
              {(dataFields.email.meta?.error && t(dataFields.email.meta?.error)) || t('common.select_value_error')}
            </Error>
          )}
        </div>
        <div>
          <Label>{t('cinema_rent_inquiry_form.company_or_private')} *</Label>
          <Input
            {...dataFields.company.input}
            id={dataFields.company.input.name}
            type="text"
            error={
              getPreValidationError(dataFields.company, t) ||
              getPostValidationError(errors, dataFields.company.input.name, t)
            }
          />
          {submitFailed && (dataFields.company.meta?.error || !dataFields.company.input.value.includes('')) && (
            <Error>
              {(dataFields.company.meta?.error && t(dataFields.company.meta?.error)) || t('common.select_value_error')}
            </Error>
          )}
        </div>
        <div>
          <Label>{t('cinema_rent_inquiry_form.department')}</Label>
          <Input
            {...dataFields.department.input}
            id={dataFields.department.input.name}
            type="text"
            error={
              getPreValidationError(dataFields.department, t) ||
              getPostValidationError(errors, dataFields.department.input.name, t)
            }
          />
        </div>
        <div>
          <Label>{t('cinema_rent_inquiry_form.street_number')}</Label>
          <Input
            {...dataFields.street.input}
            id={dataFields.street.input.name}
            type="text"
            error={
              getPreValidationError(dataFields.street, t) ||
              getPostValidationError(errors, dataFields.street.input.name, t)
            }
          />
        </div>
        <div>
          <Label>{t('cinema_rent_inquiry_form.zip_code')}</Label>
          <Input
            {...dataFields.zipCode.input}
            id={dataFields.zipCode.input.name}
            type="text"
            error={
              getPreValidationError(dataFields.zipCode, t) ||
              getPostValidationError(errors, dataFields.zipCode.input.name, t)
            }
          />
        </div>
        <div>
          <Label>{t('form.city')}</Label>
          <Input
            {...dataFields.city.input}
            id={dataFields.city.input.name}
            type="text"
            error={
              getPreValidationError(dataFields.city, t) ||
              getPostValidationError(errors, dataFields.city.input.name, t)
            }
          />
        </div>
      </div>
    </div>
  );
};

DataInputs.propTypes = propTypes;
DataInputs.defaultProps = defaultProps;

export default DataInputs;
